// Board games on screen (desktop + phone): the join prompt, the lobby with
// the game's options (computer level, clock), then a 2D board (tap a piece,
// then its destination — your side always at the bottom), clocks, the move
// list, resign / draw, and the game-over block. VR uses BoardVRPanel + the
// 3D board.

import React, { useEffect, useRef, useState } from 'react';
import { tableAction, type TableState } from '../gameStore';
import { JoinPrompt, LobbyPanel, HelpButton, GameOverBlock, PanelError, leaveTable } from '../kit/LobbyKit';
import { fmtClock, moveLines, optionValues, useBoardPicker, type BoardGameUI } from './boardGame';
import type { OverlayProps } from '../types';
import './BoardOverlay.css';

function OptionRows({ ui, pid, st, busy }: { ui: BoardGameUI; pid: string; st: TableState; busy: boolean }) {
  const vals = optionValues(ui, st);
  return (
    <span className="gx-bd-opts">
      {ui.options.map((o) => (
        <span key={o.key} className="gx-bd-opt" role="group" aria-label={o.label}>
          <span className="gx-bd-opt-label">{o.label}</span>
          {o.values.map((v) => (
            <button key={v.v} className={`gx-bd-chip ${vals[o.key] === v.v ? 'is-on' : ''}`} aria-pressed={vals[o.key] === v.v} disabled={busy}
              onClick={() => void tableAction(pid, 'configure', { options: { [o.key]: v.v } })}>{v.label}</button>
          ))}
        </span>
      ))}
    </span>
  );
}

function useTick(on: boolean) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!on) return;
    const t = window.setInterval(() => force((n) => n + 1), 250);
    return () => window.clearInterval(t);
  }, [on]);
}

export function Board2D({ ui, g, seat, pid, interactive }: { ui: BoardGameUI; g: any; seat: number; pid: string; interactive: boolean }) {
  const rules = interactive ? ui.rules(g, seat) : null;
  const { path, targets, promo, tap, choosePromo, clear } = useBoardPicker(pid, rules);
  const bySq = new Map(ui.pieces(g).map((p) => [p.sq, p]));
  const last = new Set(ui.last(g));
  const check = ui.check(g);
  const tgt = new Set(targets);
  const flip = seat === 1;
  const cells: number[] = [];
  for (let row = 0; row < 8; row++) for (let col = 0; col < 8; col++) {
    const rank = flip ? row : 7 - row, file = flip ? 7 - col : col;
    cells.push(rank * 8 + file);
  }
  return (
    <div className="gx-bd-wrap">
      <div className={`gx-bd-board ${rules ? 'is-live' : ''}`} role="grid" aria-label="Board">
        {cells.map((sq) => {
          const p = bySq.get(sq);
          const dark = ((sq & 7) + (sq >> 3)) % 2 === 0;
          const cls = ['gx-bd-sq', dark ? 'is-dark' : 'is-light', last.has(sq) ? 'is-last' : '', check === sq ? 'is-check' : '',
            path.includes(sq) ? 'is-picked' : '', tgt.has(sq) ? (p ? 'is-target is-capture' : 'is-target') : ''].join(' ');
          const clickable = !!rules && (rules.starts.has(sq) || tgt.has(sq) || path.length > 0);
          return (
            <button key={sq} className={cls} disabled={!clickable} onClick={() => tap(sq)} aria-label={`square ${sq}`}>
              {p && <span className={`gx-bd-piece side-${p.side}`}>{ui.glyph(p)}</span>}
            </button>
          );
        })}
      </div>
      {promo && ui.promoChoices && (
        <div className="gx-bd-promo" role="dialog" aria-label="Promote to">
          <span className="gx-sp-note">Promote to</span>
          {ui.promoChoices.map((c) => <button key={c.v} className="gx-sp-btn" onClick={() => choosePromo(c.v)}>{c.label}</button>)}
          <button className="gx-sp-btn gx-sp-btn--ghost" onClick={clear}>Cancel</button>
        </div>
      )}
    </div>
  );
}

export function BoardOverlay(props: OverlayProps & { ui: BoardGameUI; footer: string }) {
  const { ui, def, pid, entry, table, seat, onHelp, footer } = props;
  const st = table.state;
  const g = st.g;
  const [confirm, setConfirm] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const playing = st.phase === 'playing' && !!g;
  const clocks = g && ui.clocks ? ui.clocks(g, Date.now()) : null;
  useTick(!!clocks && playing);
  const nMoves = g ? ui.moves(g).length : 0;
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [nMoves]);
  useEffect(() => { setConfirm(false); }, [nMoves, st.phase]);

  if (seat < 0) return <JoinPrompt {...props} />;
  if (st.phase === 'lobby') {
    return <LobbyPanel {...props} seatLabel={(i) => `${def.seatNames?.[i] ?? `Seat ${i + 1}`}${i === seat ? ' · you' : ''}`}
      footer={<><OptionRows ui={ui} pid={pid} st={st} busy={entry.busy} /> {footer}</>} />;
  }
  if (!g) return null;

  const opp = 1 - seat;
  const turn = ui.turn(g);
  const myTurn = playing && turn === seat;
  const res = ui.result(g);
  const offer = ui.drawOffer(g);
  const nameOf = (i: number) => (i === seat ? 'You' : st.seats[i]?.name ?? def.seatNames?.[i] ?? `Seat ${i + 1}`);
  const side = (i: number) => def.seatNames?.[i] ?? '';
  const status = res ? res.text : myTurn ? 'Your move' : `${nameOf(opp)} to move`;
  const act = (type: string) => void tableAction(pid, 'move', { move: { type } });

  const Player = ({ i }: { i: number }) => (
    <div className={`gx-bd-player ${turn === i && playing ? 'is-turn' : ''}`}>
      <span className="gx-bd-player-name">{nameOf(i)}{st.seats[i]?.kind === 'bot' ? ' (computer)' : ''} <small>{side(i)}</small></span>
      {clocks && <span className={`gx-bd-clock ${clocks[i] < 30_000 ? 'is-low' : ''} ${turn === i && playing ? 'is-running' : ''}`} role="timer">{fmtClock(clocks[i])}</span>}
    </div>
  );

  return (
    <div className={`gx-sp gx-sp--panel gx-sp--game gx-bd ${myTurn ? 'is-my-turn' : ''}`} role="region" aria-label={`${def.label} game`}>
      <div className="gx-sp-head">
        <span className={`gx-sp-status ${myTurn ? 'is-mine' : ''}`}>{status}</span>
        <div className="gx-sp-head-actions">
          <HelpButton def={def} onHelp={onHelp} compact />
          <button className="gx-sp-btn gx-sp-btn--ghost" onClick={() => leaveTable(pid)}>Leave</button>
        </div>
      </div>
      <Player i={opp} />
      <Board2D ui={ui} g={g} seat={seat} pid={pid} interactive={playing && !entry.busy} />
      <Player i={seat} />
      {nMoves > 0 && (
        <div className="gx-bd-moves" ref={listRef} aria-label="Moves">
          {moveLines(ui.moves(g)).map((l, i) => <span key={i}>{l}</span>)}
        </div>
      )}
      {playing && (
        <div className="gx-sp-actions">
          {offer === opp ? <>
            <span className="gx-sp-note">{nameOf(opp)} offers a draw</span>
            <button className="gx-sp-btn gx-sp-btn--primary" disabled={entry.busy} onClick={() => act('accept_draw')}>Accept draw</button>
            <button className="gx-sp-btn" disabled={entry.busy} onClick={() => act('decline_draw')}>Decline</button>
          </> : offer === seat ? <span className="gx-sp-note">Draw offered…</span>
            : <button className="gx-sp-btn" disabled={entry.busy} onClick={() => act('offer_draw')}>Offer draw</button>}
          {confirm
            ? <><button className="gx-sp-btn gx-bd-danger" disabled={entry.busy} onClick={() => act('resign')}>Yes, resign</button>
              <button className="gx-sp-btn gx-sp-btn--ghost" onClick={() => setConfirm(false)}>No</button></>
            : <button className="gx-sp-btn" disabled={entry.busy} onClick={() => setConfirm(true)}>Resign</button>}
        </div>
      )}
      {st.phase === 'over' && res && (
        <GameOverBlock {...props} summary={res.winner === null ? 'Draw' : res.winner === seat ? 'You win!' : 'You lose'} />
      )}
      {st.note && playing && <span className="gx-sp-note gx-sp-log">{st.note}</span>}
      <PanelError entry={entry} />
    </div>
  );
}

export default BoardOverlay;
